import classNames from 'classnames';
import { Avatar, Button, Label, Modal, TextInput, Textarea, Tooltip } from 'flowbite-react';
import React, { FormEvent, useEffect, useState } from 'react';
import { Img } from 'react-image';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select, { MultiValue, SingleValue } from 'react-select';
import { Dropdown, gradYearOptions, locationPreferenceOptions, stringAvatar } from 'shared-utils';
import { editProfileGradYearFn, editProfileInfoFn } from 'src/apis/profile';
import UploadProfileImage from 'src/components/profile/UploadProfileImage';
import Container from 'src/components/shared/Container';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetUserProfile } from 'src/hooks/profile.hooks';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { CompanyType, EditUserProfileInfo } from 'types';
import { Loader } from 'ui';
import { HiExclamation } from 'react-icons/hi';
import { RxCross2 } from 'react-icons/rx';
import ProfileWorkExperience from 'src/components/profile/ProfileWorkExperience';
import ProfileProjects from 'src/components/profile/ProfileProjects';
import ProfileEducation from 'src/components/profile/ProfileEducation';
import ProfileCertificate from 'src/components/profile/ProfileCertificate';
import { createApplicationV2 } from 'src/apis/application';
import LinkedInResumeModal from 'src/components/my-applications/LinkedInResumeModal';
import { get } from 'lodash';

const EditProfileFromLinkedIn = () => {
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { user, isRecruiterLoggedIn } = useUserContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const positionId = searchParams.get('position');

	const [isSelf, setIsSelf] = useState<boolean>(false);
	const isPublicProfile = isRecruiterLoggedIn;
	const [isValidSalary, setIsValidSalary] = useState<boolean>(true);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const [userPreferencedLocation, setUserPreferencedLocation] = useState<Array<string>>([]);
	const [userCompanyTypePreference, setUserCompanyTypePreference] = useState<Array<CompanyType>>([]);
	const [isMoreThanFiveLocationSelected, setIsMoreThanFiveLocationSelected] = useState<boolean>(false);
	const [isMoreThanThreeComapnyTypeSelected, setIsMoreThanThreeComapnyTypeSelected] = useState<boolean>(false);
	const [profileUpdateInfo, setProfileUpdateInfo] = useState<EditUserProfileInfo>({});
	const [isLinkedInResumeModalOpen, setIsLinkedInResumeModalOpen] = useState(false);
	let locationTimeoutId: NodeJS.Timeout | undefined;
	let companyTypeTimeoutId: NodeJS.Timeout | undefined;

	const userProfileData = useGetUserProfile(user?.handler || '');

	const handleSubmitUser = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		updateUserProfileByIdFn.mutate({
			description: profileUpdateInfo.description,
			// graduationYear: profileUpdateInfo.graduationYear,
			isImmediateJoiner: profileUpdateInfo.isImmediateJoiner,
			githubURL: profileUpdateInfo.githubURL,
			linkedinURL: profileUpdateInfo.linkedinURL,
			preferredWorkLocations: userPreferencedLocation,
			preferredCompanyTypes: userCompanyTypePreference,
			currentCTC: profileUpdateInfo.currentCTC,
			currentLocation: profileUpdateInfo.currentLocation,
			desiredCTC: profileUpdateInfo.desiredCTC,
			portfolioLink: profileUpdateInfo.portfolioLink,
			workExperience: profileUpdateInfo.workExperience,
		});
	};

	useEffect(() => {
		if (userProfileData.data?.userDetail && user?.handler) {
			const flag = userProfileData.data.userDetail.handler === user?.handler ? true : false;
			setIsSelf(flag);
		}
	}, [userProfileData.data, user?.handler]);

	const handleSubmitGradYear = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!profileUpdateInfo.graduationYear) return;
		updateUserGradYearFn.mutate({
			gradYear: profileUpdateInfo?.graduationYear,
		});
	};

	const handleLoactionPreferences = (value: MultiValue<Dropdown>) => {
		const selectedLocation = value.map(item => item.value);
		if (selectedLocation.length <= 5) {
			setIsMoreThanFiveLocationSelected(false);
			setUserPreferencedLocation(selectedLocation);
		} else {
			setIsMoreThanFiveLocationSelected(true);
			if (locationTimeoutId) {
				clearTimeout(locationTimeoutId); // Clear the previous timeout
			}
			locationTimeoutId = setTimeout(() => {
				setIsMoreThanFiveLocationSelected(false);
			}, 3000);
		}
	};
	const handleCompanyTypePreferences = (value: MultiValue<Dropdown>) => {
		const selectedCompanyPreference = value.map(item => item.value as CompanyType);
		if (selectedCompanyPreference.length <= 3) {
			setIsMoreThanThreeComapnyTypeSelected(false);
			setUserCompanyTypePreference(selectedCompanyPreference);
		} else {
			setIsMoreThanThreeComapnyTypeSelected(true);
			if (companyTypeTimeoutId) {
				clearTimeout(companyTypeTimeoutId); // Clear the previous timeout
			}
			companyTypeTimeoutId = setTimeout(() => {
				setIsMoreThanThreeComapnyTypeSelected(false);
			}, 3000);
		}
	};

	const updateUserProfileByIdFn = useMutation({
		mutationKey: 'editUserProfileInfo',
		mutationFn: editProfileInfoFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);

			if (positionId) {
				console.log('user?.profile?.resumeLink:', userProfileData.data?.userDetail?.profile?.resumeLink);
				if (!userProfileData.data?.userDetail?.profile?.resumeLink) {
					setIsLinkedInResumeModalOpen(true);
				} else {
					createApplicationMutation.mutate({
						createInfo: {
							position: positionId || '',
							user: userProfileData?.data?.userDetail?._id || '',
						},
					});
				}
			} else {
				toast({
					title: 'Profile updated successfully',
					duration: 3000,
					type: 'success',
				});
				navigate(-1);
			}
		},
		onError(error: Error) {
			toast({
				title: `Error occured while updating user  ${error?.message}`,
				type: 'error',
			});
		},
	});

	const updateUserGradYearFn = useMutation({
		mutationKey: 'editProfileGradYearFn',
		mutationFn: editProfileGradYearFn,
		onSuccess(data) {
			queryClient.refetchQueries(['getUserProfile']);
			queryClient.refetchQueries(['verifyCookie']);
			toast({
				title: 'Your graduation year is updated successfully',
				duration: 3000,
				type: 'success',
			});
		},
		onError(error: Error) {
			toast({
				title: `Error occured while updating grad year ${error.message}`,
				type: 'error',
			});
		},
	});

	const createApplicationMutation = useMutation(['createApplication'], createApplicationV2, {
		onSuccess() {
			toast({
				title: 'Profile is updated and Application created successfully',
				type: 'success',
			});
			queryClient.refetchQueries(['getAllApplications']);
			navigate(-1);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured while creating application ${errorMessage}`,
				type: 'error',
			});
			queryClient.refetchQueries(['getAllApplications']);
			navigate(-1);
		},
	});

	useEffect(() => {
		if (userProfileData.data?.userDetail) {
			setProfileUpdateInfo({
				...profileUpdateInfo,
				description: userProfileData.data?.userDetail?.profile.profileDescription || '',
				graduationYear: userProfileData.data?.userDetail?.profile.gradYear,
				isImmediateJoiner: userProfileData.data?.userDetail?.profile.isImmediateJoiner ?? '',
				linkedinURL: userProfileData.data?.userDetail?.profile.linkedInProfile || '',
				githubURL: userProfileData.data?.userDetail?.profile.githubURL || '',
				portfolioLink: userProfileData.data?.userDetail.profile.portfolioLink || '',
				desiredCTC: userProfileData.data?.userDetail.profile.expectedSalary,
				currentLocation: userProfileData.data?.userDetail.profile.userLocation,
				workExperience: userProfileData.data?.userDetail.profile.totalExperience,
				currentCTC: userProfileData.data?.userDetail.profile.currentSalary,
			});
		}

		if (userProfileData.data?.userDetail && userProfileData.data?.userDetail.profile?.preferredCompanyTypes) {
			setUserCompanyTypePreference(userProfileData.data?.userDetail.profile?.preferredCompanyTypes as CompanyType[]);
		}

		if (userProfileData.data?.userDetail && userProfileData.data?.userDetail.profile?.preferredJobLocations) {
			setUserPreferencedLocation(userProfileData.data?.userDetail.profile?.preferredJobLocations);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userProfileData.data?.userDetail]);

	if (userProfileData.isLoading) return <Loader />;
	if (!userProfileData) return <div> There is nothing to edit in your Profile.</div>;

	return (
		<Container className="!pb-[100px]">
			<div className="w-full flex flex-col gap-6 py-4 m-auto max-w-[1024px]">
				<div className="flex flex-col gap-y-6 rounded-xl bg-white dark:bg-[#1F2A37] px-[14px] py-3 md:flex-row md:gap-3 md:p-6">
					{/* Right Side Section */}
					<div className="flex w-full flex-col gap-4 md:mt-6 md:w-[40%]">
						{/* Avatar Container */}
						<div className="relative flex items-center justify-center md:items-center">
							<Avatar
								alt="User Profile"
								placeholderInitials={stringAvatar(userProfileData.data?.userDetail?.name.trim().toUpperCase() || '')}
								rounded={true}
								img={
									userProfileData.data?.userDetail?.profile.profileImageUrl ||
									userProfileData.data?.userDetail?.profile.imageUrl ||
									''
								}
								size={'xl'}
								bordered
								className={classNames(
									!userProfileData.data?.userDetail?.profile?.profileImageUrl && 'profile-avatar-div',
									'text-5xl hidden md:block',
								)}
							/>
							<Avatar
								alt="User Profile"
								placeholderInitials={stringAvatar(userProfileData.data?.userDetail?.name.trim().toUpperCase() || '')}
								rounded={true}
								img={userProfileData.data?.userDetail?.profile?.profileImageUrl || ''}
								size={'lg'}
								bordered
								className={classNames(
									!userProfileData.data?.userDetail?.profile?.profileImageUrl && 'profile-avatar-div',
									'text-3xl md:hidden',
								)}
							/>
							<Img
								className="absolute bottom-[-12px] left-0 right-0 mx-auto cursor-pointer max-w-[20px] max-h-[20px] md:max-w-[30px] md:max-h-[30px]"
								src={'/assets/addIcon.svg'}
								alt="addIcon"
								onClick={onOpen}
							/>
						</div>
						{/* User Name, email, phone info Container*/}
						<div className="flex w-full flex-col items-center gap-[2px]">
							<div className="flex flex-col justify-center dark:text-white">
								{/* Adding tootlip for user name */}
								{userProfileData.data?.userDetail && userProfileData.data?.userDetail?.name.length > 21 ? (
									<Tooltip content={userProfileData.data?.userDetail?.name}>
										<p className="mx-auto w-full max-w-[200px] truncate text-center font-inter text-[24px] font-[700] md:max-w-[265px]">
											{userProfileData.data?.userDetail?.name}
										</p>
									</Tooltip>
								) : (
									<p className="font-inter text-[24px] font-[700]">{userProfileData.data?.userDetail?.name}</p>
								)}
							</div>
							<div className="text-[16px] dark:text-[#d2d2d7]">{userProfileData.data?.userDetail?.phone || ''}</div>
							<div className="flex flex-col justify-center text-[16px] text-gray-600 dark:text-[#d2d2d7]">
								{/* Adding tootlip for user email */}
								{userProfileData.data?.userDetail && userProfileData.data?.userDetail?.email.length > 24 ? (
									<Tooltip content={userProfileData.data?.userDetail?.email}>
										<p className="mx-auto w-full max-w-[200px] truncate text-center font-inter text-[20px] font-[500] md:max-w-[265px]">
											{userProfileData.data?.userDetail?.email}
										</p>
									</Tooltip>
								) : (
									<p className="font-inter font-[500]">{userProfileData.data?.userDetail?.email}</p>
								)}
							</div>
						</div>
					</div>
					{/* Left Side Section */}
					<div className="w-full md:w-[60%] md:px-4">
						<form className="flex flex-col gap-2 text-white md:gap-3" onSubmit={handleSubmitUser}>
							{/* Basic Details Heading */}
							<div className="border-b-2 border-[#7c85a5] pb-2">
								<p className="font-[600] leading-[24px] text-[#6698FA]">BASIC DETAILS</p>
							</div>
							{/* User Name Input */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label className="font-inter text-[14px] font-[400] dark:text-white" htmlFor="name" value="Name" />
								</div>
								<TextInput
									style={{
										fontFamily: "'Inter', sans-serif",
										fontSize: '14px',
									}}
									id="name"
									name="name"
									value={userProfileData.data?.userDetail?.name || ''}
									placeholder="John Doe"
									disabled
								/>
							</div>
							{/* User Work Experience Dropdown */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="work_experience "
										value="Work Experience (In years)"
									/>
								</div>
								<div>
									<TextInput
										disabled={userProfileData.data?.userDetail?.profile?.totalExperience !== null ? true : false}
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="number"
										id="work_experience"
										name="work_experience"
										placeholder="0"
										min={0}
										value={profileUpdateInfo.workExperience}
										onChange={e =>
											setProfileUpdateInfo({ ...profileUpdateInfo, workExperience: Number(e.target.value) })
										}
									/>
								</div>
							</div>
							{/* User Greaduation Year Input*/}
							<div className="mb-[8px] flex justify-between items-end gap-2">
								<div className="w-full">
									<div className="mb-[5px]">
										<Label
											className="font-inter text-[14px] font-[400] dark:text-white"
											htmlFor="graduation_year "
											value="Graduation Year "
										/>
									</div>
									<div className="mt-1">
										<Select
											// isDisabled={userProfileData.data?.userDetail?.profile?.gradYear ? true : false}
											menuPortalTarget={document.body}
											classNamePrefix="my-select-plans"
											instanceId="graduation_year"
											className="single-value-select-tag"
											closeMenuOnSelect={true}
											isMulti={false}
											value={gradYearOptions.find(o => Number(o.value) === profileUpdateInfo.graduationYear)}
											options={gradYearOptions}
											onChange={(val: SingleValue<Dropdown>) => {
												setProfileUpdateInfo({
													...profileUpdateInfo,
													graduationYear: Number(val?.value),
												});
											}}
										/>
									</div>
								</div>

								<div className="">
									<Button
										color="purplePlaced"
										disabled={userProfileData.data?.userDetail?.profile?.gradYear === profileUpdateInfo?.graduationYear}
										onClick={() => setShowConfirmModal(true)}
									>
										Update
									</Button>
									<Modal size="md" show={showConfirmModal} onClose={() => setShowConfirmModal(false)} dismissible>
										<Modal.Body className="relative font-inter flex flex-col gap-y-8 justify-center rounded-[8px] border border-solid dark:border-[#555770] bg-white dark:bg-[#18222D] px-4 py-[18px]">
											<form onSubmit={handleSubmitGradYear}>
												<div className="flex items-center gap-[10px] mb-[27px]">
													<div className="flex items-center gap-[10px]">
														<HiExclamation className="h-6 w-6 text-[#ffff00]" />
														<h1 className="text-xl font-bold">Important note</h1>
													</div>
													<RxCross2
														onClick={() => setShowConfirmModal(false)}
														className="absolute right-[16px] top-[16px] h-[20px] w-[20px] cursor-pointer text-[#9CA3AF]"
													/>
												</div>

												<p className="text-center text-sm font-medium dark:text-[#E4E4EB] ">
													Updating your graduation year may result in your path being reassigned.
												</p>

												<p className="text-center text-sm font-medium dark:text-[#E4E4EB] mt-2">
													Are you sure to update graduation year?
												</p>

												<div className="flex gap-4 text-center justify-center mt-6 pb-6">
													<Button color="purplePlaced" className="font-inter font-semibold" type="submit">
														Yes, I’m sure{' '}
													</Button>
													<Button
														color="light"
														type="button"
														className="font-inter font-semibold"
														onClick={() => setShowConfirmModal(false)}
													>
														No, cancel
													</Button>
												</div>
											</form>
										</Modal.Body>
									</Modal>
								</div>
							</div>
							{/* User Current Location */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="current_location "
										value="Current Location"
									/>
								</div>
								<div>
									<Select
										// className="react-select-container"
										menuPlacement={'auto'}
										classNamePrefix="my-select-plans"
										instanceId="current_location"
										className="single-value-select-tag"
										closeMenuOnSelect={true}
										placeholder="Mumbai, Maharashtra"
										isMulti={false}
										value={
											profileUpdateInfo.currentLocation
												? {
														label: profileUpdateInfo.currentLocation,
														value: profileUpdateInfo.currentLocation,
												  }
												: null
										}
										options={locationPreferenceOptions.map(location => ({ value: location, label: location }))}
										onChange={(val: SingleValue<Dropdown>) => {
											setProfileUpdateInfo({
												...profileUpdateInfo,
												currentLocation: val?.value,
											});
										}}
									/>
								</div>
							</div>
							{/* User Profile Description */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="description"
										value="Description"
									/>
								</div>
								<Textarea
									style={{
										fontFamily: "'Inter', sans-serif",
										height: '100px',
										fontSize: '14px',
									}}
									id="description"
									name="description"
									maxLength={512}
									value={profileUpdateInfo.description || ''}
									onChange={e =>
										setProfileUpdateInfo({
											...profileUpdateInfo,
											description: e.target.value,
										})
									}
								/>
							</div>
							{/* User Portfolio/Website Link */}
							<div className="mb-[8px]">
								<div className="mb-[5px]">
									<Label
										className="font-inter text-[14px] font-[400] dark:text-white"
										htmlFor="portfolio_link "
										value="Portfolio/Website Link"
									/>
								</div>
								<div>
									<TextInput
										style={{
											fontFamily: "'Inter', sans-serif",
										}}
										type="url"
										id="portfolio_link"
										name="portfolio_link"
										placeholder="https://www.website.com/"
										value={profileUpdateInfo.portfolioLink || ''}
										onChange={e => setProfileUpdateInfo({ ...profileUpdateInfo, portfolioLink: e.target.value })}
									/>
								</div>
							</div>

							{/* Submit Button */}
							<div className="fixed bg-white dark:bg-[#18222D] z-[1] bottom-0 inset-x-0 py-3 px-4 flex justify-start md:flex-row-reverse w-full gap-5">
								<Button
									color="purplePlaced"
									type="submit"
									className="w-full md:max-w-[300px]"
									disabled={updateUserProfileByIdFn.isLoading || createApplicationMutation.isLoading || !isValidSalary}
								>
									{updateUserProfileByIdFn.isLoading ? <Loader /> : positionId ? 'Save & Apply' : 'Save'}
								</Button>
								<Button className="w-[87px] !bg-[#555770] hover:!bg-[#44465b]" onClick={() => navigate(-1)}>
									Cancel
								</Button>
							</div>
						</form>
					</div>
					<UploadProfileImage isOpen={isOpen} onClose={onClose} />
				</div>

				<div>
					<ProfileWorkExperience
						isSelf={isSelf}
						title="Work Experience"
						isPublicProfile={isPublicProfile}
						workExperience={userProfileData?.data?.workExperience ?? []}
					/>
				</div>

				<div>
					<ProfileProjects
						userProjects={userProfileData?.data?.projectDetails ?? []}
						isSelf={isSelf}
						title="Projects"
						isPublicProfile={isPublicProfile}
					/>
				</div>

				<div>
					<ProfileEducation
						isSelf={isSelf}
						title="Education"
						isPublicProfile={isPublicProfile}
						userEducation={userProfileData?.data?.educationDetails ?? []}
					/>
				</div>

				<div>
					<ProfileCertificate
						isSelf={isSelf}
						title="Certificates"
						isPublicProfile={isPublicProfile}
						userCertificate={userProfileData?.data?.certfifcateDetails ?? []}
					/>
				</div>
			</div>

			<LinkedInResumeModal
				positionId={positionId || ''}
				showLinkedInButton={false}
				isOpen={isLinkedInResumeModalOpen}
				onClose={() => {
					setIsLinkedInResumeModalOpen(false);
				}}
				setIsLinkedInModalOpen={() => {
					//
				}}
				createApplicationLoading={createApplicationMutation?.isLoading}
				createApplicationMutation={createApplicationMutation}
			/>
		</Container>
	);
};

export default EditProfileFromLinkedIn;
